import { mapToNewAccessService } from 'app/domain/AccessServiceTypes';
import { continueWatchingActiveSelector, continueWatchingItemsSelector } from 'app/reducers/continueWatching';
import { accessVersionChoiceSelector } from 'app/reducers/videoPage';
import { trackTitleData, trackVideoData } from 'app/utils/adform';
import { createContentoHistory } from 'app/utils/continueWatchingUtils';
import { trackCustomEvent } from 'app/utils/statistics/clientStatistics';
import { titlePlayFromBeginning, titlePlayTrailer } from 'app/utils/statistics/customEvents';
import { formatDuration } from 'app/utils/svtDateAndTime';
import { withRouter } from 'next/router';
import React from 'react';
import { connect } from 'react-redux';
import { useQuery } from 'urql';
import { updateAccessVersionChoice } from '../../actions/videoPage';
import env from '../../env';
import { fontWeightBold, regularWhite } from '../../ui-components/design-tokens';
import PlayImage from '../../ui-components/PlayImage/PlayImage';
import PlayText, { SIZE } from '../../ui-components/typography/PlayText/PlayText';
import { browserMeasurements } from '../../utils/browserMeasurements';
import { createTitleStatistics, createVideoStatistics } from '../../utils/contentStatistics';
import { errorStatistics } from '../../utils/statistics/universalStatistics';
import { contentoImageUrl, createImageProxyUrl, DEFAULT_IMAGE_LARGE } from '../../utils/svtimage';
import { ReactHelmetPlay } from '../ApplicationHandler/ReactHelmet';
import SendStatistics from '../CommonElements/SendStatistics';
import { NotFoundContainer } from '../ErrorHandler';
import ErrorContainer from '../ErrorHandler/ErrorContainer';
import FavoriteButtonHooked from '../FavoriteButton/FavoriteButtonHooked';
import { PlayLinkExternal } from '../Link';
import SimilarVideoContentContainer from '../SimilarVideoContent/SimilarVideoContentContainer';
import OpenModal from '../VideoModal/OpenModal';
import AssociatedContent from './components/AssociatedContent/AssociatedContent';
import CastAndCrew from './components/CastAndCrew/CastAndCrew';
import Categories from './components/Categories/Categories';
import EditorialMessage from './components/EditorialMessage/EditorialMessage';
import Highlights from './components/Highlights/Highlights';
import Metadata from './components/Metadata/Metadata';
import ShowMore from './components/ShowMore/ShowMore';
import {
    Byline,
    HiddenByline,
    NotAvailableInSweden,
    PlaySpan,
    StyledBlurHashCanvas,
    StyledTopArea,
    StyledTopAreaBadge,
    StyledTopAreaHeading,
    StyledTopAreaHighlightText,
    StyledTopAreaLeft,
    StyledTopAreaLeftPlaceholder,
    StyledTopAreaMeta,
    StyledTopAreaPlaceholder,
    StyledTopAreaPlayButton,
    StyledTopAreaPlayButtonArea,
    StyledTopAreaPlayButtonText,
    StyledTopAreaRight,
    StyledTopAreaRightPlaceholder,
    StyledTopAreaSubheading,
    StyledTopAreaUpcomingLive,
} from './components/TopArea/TopArea.styles';
import TopAreaVariants from './components/TopArea/TopAreaVariants';
import {
    getAssociatedContentProgress,
    getSmartStartProgress,
    getStructuredData,
    getSubheading,
} from './DetailsPage.helpers';
import {
    ContentWarning,
    Description,
    DetailArea,
    MoreDetails,
    StyledDetailsPage,
    StyledOnboarding,
    StyledProgressBar,
} from './DetailsPage.styles';
import DETAILS_PAGE_QUERY from './DetailsPageQuery.graphql';
import SMART_START_QUERY from './SmartStartQuery.graphql';
import { externalArticle } from '../../utils/statistics/customEvents';
import { Experiment, Variant } from '@svt/abtest/lib/redux'; // A/B-test Favorites rollout sep 2021

const getStatisticsTitle = (detailsPage, idOrSlug) => {
    let statisticsObject;
    if (typeof window !== 'undefined') {
        const browserSize = browserMeasurements(window);

        statisticsObject = createTitleStatistics(
            idOrSlug,
            detailsPage.item.svtId,
            detailsPage.analyticsIdentifiers.viewId,
            detailsPage.analyticsIdentifiers.viewType,
            browserSize
        );
    }

    return statisticsObject;
};

const getStatisticsVideo = (detailsPage, router) => {
    let statisticsObject;
    if (typeof window !== 'undefined') {
        const browserSize = browserMeasurements(window);

        statisticsObject = createVideoStatistics(
            router.query.titleSlug,
            router.query.episodeSlug,
            detailsPage.item.svtId,
            detailsPage.analyticsIdentifiers.viewId,
            detailsPage.analyticsIdentifiers.viewType,
            browserSize
        );
    }

    return statisticsObject;
};

const DetailsPage = ({
    idOrSlug,
    isTitle,
    router,
    updateAccessibilityVersion,
    continueWatchingIsActive,
    continueWatching,
    currentAccessibilityChoice,
}) => {
    const [result] = useQuery({
        query: DETAILS_PAGE_QUERY,
        variables: {
            id: idOrSlug,
            includeFullOppetArkiv: env.FLAG_ENABLE_ALL_OA,
        },
        requestPolicy: 'cache-first',
    });

    const [smartStartResult] = useQuery({
        query: SMART_START_QUERY,
        variables: {
            id: idOrSlug,
            history: createContentoHistory(continueWatching),
            includeFullOppetArkiv: env.FLAG_ENABLE_ALL_OA,
            accessibility: mapToNewAccessService(currentAccessibilityChoice),
        },
        requestPolicy: 'cache-and-network',
        context: React.useMemo(
            () => ({
                preferGetMethod: false,
            }),
            []
        ),
    });

    const { data, fetching, error } = result;

    const { data: smartStartData, fetching: smartStartFetching, error: smartStartError } = smartStartResult;

    const titleArticleId = data?.detailsPage?.item?.parent?.svtId;

    if (fetching) {
        return (
            <StyledDetailsPage>
                <StyledTopAreaPlaceholder>
                    <StyledTopAreaLeftPlaceholder />
                    <StyledTopAreaRightPlaceholder />
                </StyledTopAreaPlaceholder>
            </StyledDetailsPage>
        );
    }

    if (data) {
        const detailsPage = data?.detailsPage;

        if (!detailsPage) {
            return (
                <>
                    <NotFoundContainer shouldDispatchHttpStatus={true} baseContainer />
                    <SendStatistics statisticsObject={errorStatistics(404, router.asPath)} />
                </>
            );
        }

        const smartStart = smartStartData?.smartStart;

        const url = smartStart?.item.urls?.svtplay || (smartStartError && detailsPage.video?.urls?.svtplay);

        const selectedVideoId = smartStart?.videoSvtId || (smartStartError && detailsPage.video?.svtId);
        const label = smartStart?.label || (smartStartError && 'Spela');

        const blurHash = detailsPage.images?.wide?.blurHash;

        const urgentMessage = detailsPage.item?.messages?.urgent;

        const editorialMessage = detailsPage.item?.messages?.editorial;

        const categories = detailsPage.categories;

        const badge = smartStart?.badge;

        const associatedContent = detailsPage.associatedContent;

        const accessibleLabel = isTitle
            ? `${smartStart?.label} av ${detailsPage?.item?.parent?.name}`
            : `${smartStart?.label} ${detailsPage.item?.parent?.name ? detailsPage.item.parent.name : ''} `;

        const variants = smartStart?.item.videos.filter(
            (video) => video.svtId && video.svtId !== selectedVideoId
        );

        const smartStartProgress = getSmartStartProgress(
            continueWatchingIsActive,
            continueWatching?.toJS(),
            smartStart
        );

        const associatedContentProgress = getAssociatedContentProgress(
            continueWatchingIsActive,
            continueWatching?.toJS(),
            associatedContent
        );

        const positionInUrlFormated =
            router.query.position && formatDuration(Math.floor(router.query.position));

        isTitle ? trackTitleData(detailsPage) : trackVideoData(detailsPage);

        // temporary filter of highlights for internal use. SSG 13 aug 2021
        const highlights = detailsPage.item?.highlights?.filter((item) => !item.slug.startsWith('intern-'));
        // const highlights = detailsPage.item?.highlights;

        const highlightDeeplinkItem = highlights?.find(
            (item) => item.positionInSeconds === parseInt(router.query.position, 10)
        );

        const highlightDescription = highlightDeeplinkItem ? ` – ${highlightDeeplinkItem.name}` : '';

        const topImage = highlightDeeplinkItem?.thumbnailId
            ? createImageProxyUrl(highlightDeeplinkItem.thumbnailId, 0, 'large', 1024, DEFAULT_IMAGE_LARGE)
            : contentoImageUrl(detailsPage.images.wide, 'large', 1024, DEFAULT_IMAGE_LARGE);

        const isSingle = detailsPage?.item?.parent?.__typename === 'Single';
        const canonical = env.BASE_URL + detailsPage?.item?.urls?.svtplay;
        const ogCanonical = highlightDeeplinkItem?.positionInSeconds
            ? canonical + '?position=' + highlightDeeplinkItem?.positionInSeconds
            : canonical;
        const metaName =
            isTitle || isSingle || !detailsPage.item.parent
                ? `${detailsPage.item.name}${highlightDescription}`
                : `${detailsPage.item.parent.name} – ${detailsPage.item.name}${highlightDescription}`;

        const clips = associatedContent.find((a) => a.selectionType === 'clips');
        const firstTrailer = clips?.items.find((a) => a.item.__typename === 'Trailer');

        const trailerVideoId = firstTrailer && firstTrailer?.item.videoSvtId;
        const trailerUrl = firstTrailer && firstTrailer?.item.urls.svtplay;

        const frontMoreThanTrailer = trailerVideoId !== selectedVideoId;

        const isLive = smartStart?.item?.live?.liveNow;

        const handleExternalLinkClick = (href) => {
            trackCustomEvent(externalArticle(href));
        };

        return (
            <StyledDetailsPage itemScope={true}>
                <ReactHelmetPlay
                    pathForFeeds={router.asPath}
                    title={metaName}
                    description={detailsPage.description}
                    image={topImage}
                >
                    <meta
                        property="og:type"
                        key="og:type"
                        content={isTitle ? 'video.tv_show' : 'video.episode'}
                    />
                    <meta property="og:image" key="og:image" content={topImage} />
                    <meta name="twitter:image:src" key="twitter:image:src" content={topImage} />
                    <meta property="og:url" key="og:url" content={ogCanonical} />
                    <meta name="twitter:url" key="twitter:url" content={ogCanonical} />
                    <link rel="canonical" key="canonical" href={canonical} />
                    <script type="application/ld+json">
                        {getStructuredData(detailsPage, topImage, url, isLive)}
                    </script>
                </ReactHelmetPlay>
                <StyledTopArea>
                    {blurHash && <StyledBlurHashCanvas hash={blurHash} width={256} height={144} punch={1} />}
                    <StyledTopAreaLeft>
                        <PlayImage src={topImage} />
                        {smartStartProgress ? (
                            <StyledProgressBar progress={smartStartProgress} selected />
                        ) : null}
                    </StyledTopAreaLeft>
                    <StyledTopAreaRight>
                        <StyledTopAreaMeta>
                            {badge && (
                                <StyledTopAreaBadge
                                    text={badge.text}
                                    theme={badge.type}
                                    accessibilityText={badge.altText}
                                    overlay={false}
                                />
                            )}
                            {isSingle && (
                                <Byline size={SIZE.SMALL} fontColor={regularWhite} aria-hidden="true">
                                    {detailsPage.item.shortDescription}
                                </Byline>
                            )}
                            <StyledTopAreaHeading>
                                {detailsPage.item?.parent?.name
                                    ? detailsPage.item.parent.name
                                    : detailsPage.item.name}
                            </StyledTopAreaHeading>
                            <StyledTopAreaSubheading data-rt="video-player-splash-play">
                                {getSubheading(detailsPage.item, isTitle)}
                            </StyledTopAreaSubheading>

                            {highlightDeeplinkItem && (
                                <StyledTopAreaHighlightText type={SIZE.LARGE}>
                                    {`${positionInUrlFormated}: ${highlightDeeplinkItem.name}`}
                                </StyledTopAreaHighlightText>
                            )}
                            {isSingle && <HiddenByline>{detailsPage.item.shortDescription}</HiddenByline>}

                            <StyledTopAreaPlayButtonArea
                                onlyAvailableInSweden={
                                    detailsPage.moreDetails.restrictions.onlyAvailableInSweden
                                }
                                playFromPosition={positionInUrlFormated ? true : false}
                            >
                                {url && (
                                    <>
                                        <OpenModal
                                            videoId={selectedVideoId}
                                            videoUrl={url}
                                            position={router.query.position}
                                            extraQuery={
                                                highlightDeeplinkItem && {
                                                    highlight: highlightDeeplinkItem?.slug,
                                                }
                                            }
                                        >
                                            {(onClick) => (
                                                <StyledTopAreaPlayButton
                                                    role="button"
                                                    data-rt="top-area-play-button"
                                                    onlyAvailableInSweden={
                                                        detailsPage.moreDetails.restrictions
                                                            .onlyAvailableInSweden
                                                    }
                                                    playFromPosition={positionInUrlFormated ? true : false}
                                                    onClick={onClick}
                                                >
                                                    <StyledTopAreaPlayButtonText aria-label={accessibleLabel}>
                                                        {positionInUrlFormated
                                                            ? `Spela från ${positionInUrlFormated}`
                                                            : label}
                                                    </StyledTopAreaPlayButtonText>
                                                </StyledTopAreaPlayButton>
                                            )}
                                        </OpenModal>
                                        <NotAvailableInSweden
                                            weight={fontWeightBold}
                                            onlyAvailableInSweden={
                                                detailsPage.moreDetails.restrictions.onlyAvailableInSweden
                                            }
                                        >
                                            Programmet kan bara ses i Sverige
                                        </NotAvailableInSweden>
                                    </>
                                )}
                                {detailsPage.isUpcomingLive && !url && (
                                    <StyledTopAreaUpcomingLive>
                                        Sändningen startar {detailsPage.item.validFromFormatted.toLowerCase()}
                                    </StyledTopAreaUpcomingLive>
                                )}
                                {isTitle && trailerUrl && frontMoreThanTrailer && !isLive && (
                                    <>
                                        <OpenModal videoId={trailerVideoId} videoUrl={trailerUrl}>
                                            {(onClick) => {
                                                const handlePlayTrailer = (e) => {
                                                    trackCustomEvent(titlePlayTrailer(trailerVideoId));
                                                    onClick(e);
                                                };

                                                return (
                                                    <StyledTopAreaPlayButton
                                                        role="button"
                                                        data-rt="top-area-trailer-button"
                                                        onlyAvailableInSweden={
                                                            detailsPage.moreDetails.restrictions
                                                                .onlyAvailableInSweden
                                                        }
                                                        secondaryButton
                                                        playFromPosition={
                                                            positionInUrlFormated ? true : false
                                                        }
                                                        onClick={handlePlayTrailer}
                                                    >
                                                        <StyledTopAreaPlayButtonText
                                                            aria-label={'Spela trailer för programmet'}
                                                        >
                                                            Spela trailer
                                                        </StyledTopAreaPlayButtonText>
                                                    </StyledTopAreaPlayButton>
                                                );
                                            }}
                                        </OpenModal>
                                    </>
                                )}
                                {isLive && (
                                    <>
                                        <OpenModal videoId={selectedVideoId} videoUrl={url} position={'1'}>
                                            {(onClick) => {
                                                const handlePlayFromStart = (e) => {
                                                    trackCustomEvent(titlePlayFromBeginning(selectedVideoId));
                                                    onClick(e);
                                                };

                                                return (
                                                    <StyledTopAreaPlayButton
                                                        role="button"
                                                        data-rt="top-area-play-button"
                                                        onlyAvailableInSweden={
                                                            detailsPage.moreDetails.restrictions
                                                                .onlyAvailableInSweden
                                                        }
                                                        secondaryButton
                                                        previousIcon
                                                        playFromPosition={
                                                            positionInUrlFormated ? true : false
                                                        }
                                                        onClick={handlePlayFromStart}
                                                    >
                                                        <StyledTopAreaPlayButtonText
                                                            aria-label={'Spela direktsändning från början'}
                                                        >
                                                            <PlaySpan>Spela </PlaySpan> från början
                                                        </StyledTopAreaPlayButtonText>
                                                    </StyledTopAreaPlayButton>
                                                );
                                            }}
                                        </OpenModal>
                                    </>
                                )}
                            </StyledTopAreaPlayButtonArea>

                            <TopAreaVariants variants={variants} onClick={updateAccessibilityVersion} />
                        </StyledTopAreaMeta>
                    </StyledTopAreaRight>
                </StyledTopArea>
                <MoreDetails>
                    <DetailArea position="left">
                        {urgentMessage && <EditorialMessage {...urgentMessage} urgent boldText />}
                        <Description size={SIZE.LARGE}>{detailsPage.description}</Description>
                        {detailsPage?.contentWarning && (
                            <ContentWarning size={SIZE.LARGE} weight={fontWeightBold}>
                                {detailsPage.contentWarning.text}
                            </ContentWarning>
                        )}

                        <Experiment
                            projectId="svtplay"
                            slug="favorites"
                            defaultVariant="with-favorites"
                            timeout={2000}
                        >
                            <Variant slug="without-favorites">
                                <></>
                            </Variant>
                            <Variant slug="with-favorites">
                                <>
                                    <StyledOnboarding id="favorites">
                                        <PlayText>
                                            Nu går det att spara dina favoritprogram! Dina sparade program
                                            hittar du sedan i "Min lista" på startsidan.{' '}
                                            <PlayLinkExternal
                                                isExternal={true}
                                                url={
                                                    'https://kontakt.svt.se/guide/sa-fungerar-min-lista-och-att-spara-program-pa-svtplayse'
                                                }
                                                onClick={(e) =>
                                                    handleExternalLinkClick(
                                                        'https://kontakt.svt.se/guide/sa-fungerar-min-lista-och-att-spara-program-pa-svtplayse'
                                                    )
                                                }
                                            >
                                                För mer info se vår artikel.
                                            </PlayLinkExternal>
                                        </PlayText>
                                    </StyledOnboarding>
                                    <FavoriteButtonHooked titleId={titleArticleId} />
                                </>
                            </Variant>
                        </Experiment>

                        {highlights && (
                            <Highlights videoId={selectedVideoId} videoUrl={url} highlights={highlights} />
                        )}
                        {categories && <Categories categories={categories} />}
                        {editorialMessage && <EditorialMessage {...editorialMessage} />}
                    </DetailArea>
                    <DetailArea position="right">
                        <Metadata
                            validFrom={detailsPage.moreDetails?.validFrom}
                            validTo={detailsPage.moreDetails?.validTo}
                            productionYear={detailsPage?.moreDetails?.productionYear}
                            originalProgramTitle={detailsPage?.item?.originalProgramTitle}
                            isUpcomingVOD={detailsPage.isUpcomingVOD}
                            onlyAvailableInSwedenFormatted={
                                detailsPage.moreDetails?.restrictions?.onlyAvailableInSwedenFormatted
                            }
                            nextEpisodeAvailableFormatted={detailsPage?.item.nextEpisodeAvailableFormatted}
                        />
                    </DetailArea>
                    {!isTitle && !isSingle && detailsPage.castAndCrew?.length > 0 && (
                        <DetailArea position="left">
                            <ShowMore allByDefault={isTitle || isSingle}>
                                <CastAndCrew castAndCrew={detailsPage.castAndCrew} />
                            </ShowMore>
                        </DetailArea>
                    )}
                </MoreDetails>

                {!isSingle && (
                    <AssociatedContent
                        sections={associatedContent}
                        selectedVideoId={selectedVideoId}
                        progress={associatedContentProgress}
                    />
                )}

                {smartStartFetching && (
                    <StyledTopAreaPlaceholder>
                        <StyledTopAreaLeftPlaceholder />
                        <StyledTopAreaRightPlaceholder />
                    </StyledTopAreaPlaceholder>
                )}

                <SimilarVideoContentContainer titleArticleId={titleArticleId} />
                {isTitle ? (
                    <SendStatistics statisticsObject={getStatisticsTitle(detailsPage, idOrSlug)} />
                ) : (
                    <SendStatistics statisticsObject={getStatisticsVideo(detailsPage, router)} />
                )}
            </StyledDetailsPage>
        );
    }

    if (error) {
        return (
            <>
                <ErrorContainer pathname={router.asPath} shouldDispatchHttpStatus={true} />
                <SendStatistics statisticsObject={errorStatistics(500, router.asPath)} />
            </>
        );
    }

    return null;
};

class DetailsPageContainer extends React.Component {
    shouldComponentUpdate(nextProps) {
        return nextProps.openModal === false;
    }

    render() {
        const {
            id: idOrSlug,
            isTitle,
            router,
            updateAccessibilityVersion,
            continueWatchingIsActive,
            continueWatching,
            currentAccessibilityChoice,
        } = this.props;

        return (
            <DetailsPage
                idOrSlug={idOrSlug}
                isTitle={isTitle}
                router={router}
                updateAccessibilityVersion={updateAccessibilityVersion}
                continueWatchingIsActive={continueWatchingIsActive}
                continueWatching={continueWatching}
                currentAccessibilityChoice={currentAccessibilityChoice}
            />
        );
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        updateAccessibilityVersion: (variant) => dispatch(updateAccessVersionChoice(variant)),
    };
};

const mapStateToProps = (state, ownProps) => ({
    ...ownProps,
    continueWatchingIsActive: continueWatchingActiveSelector(state),
    continueWatching: continueWatchingItemsSelector(state),
    currentAccessibilityChoice: accessVersionChoiceSelector(state),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DetailsPageContainer));
